import React, { useContext, useEffect, useMemo, useState, useRef } from 'react';
import ReactDOMServer from 'react-dom/server';
import axios from "axios";
import { Link, navigate, useQueryParams } from 'raviger';
import { Button, ButtonGroup, Card, CardGroup, Col, Collapse, Form, FormControl, InputGroup, Modal, OverlayTrigger, Row, Spinner, Tooltip } from 'react-bootstrap';
import moment from 'moment';
import L from "leaflet";
import { Marker, Tooltip as MapTooltip, useMap } from "react-leaflet";
import { DATE_FORMAT } from '../constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowAltCircleLeft,
  faChevronDown,
  faDownload,
  faEdit,
  faEye,
  faEyeSlash,
  faFileCsv,
  faInfoCircle,
  faList,
  faMapMarkedAlt,
  faMapMarkerAlt,
  faPaw,
  faPen,
  faPlusSquare,
  faPrint,
  faSearch,
  faUserAlt,
  faUserMinus,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import {
  faPenSlash,
} from '@fortawesome/pro-solid-svg-icons';
import { faPipe } from '@fortawesome/pro-thin-svg-icons';
import { Typeahead, Menu, MenuItem } from 'react-bootstrap-typeahead';
import { useMark, useSubmitting, useFillRemainingHeight } from '../hooks';
import Header from '../components/Header';
import { titleCase } from '../components/Utils';
import Paginator from '../components/Paginator';
import Scrollbar from '../components/Scrollbars';
import { ITEMS_PER_PAGE } from '../constants';
import { SystemErrorContext } from '../components/SystemError';
import { AuthContext } from "../accounts/AccountsReducer";
import { capitalize } from '../utils/formatString';
import { printAllOwnersDetails } from './Utils';
import LoadingLink from "../components/LoadingLink";
import { Checkbox } from '../components/Form';
import ActionsDropdown from '../components/ActionsDropdown';
import Map, { AddressLookup } from '../components/Map';
import { catColorChoices, dogColorChoices, horseColorChoices, otherColorChoices, statusChoices, vetStatusChoices } from '../animals/constants';
import { faChevronDoubleDown, faChevronDoubleUp, faFileBracketsCurly } from '@fortawesome/pro-solid-svg-icons';

const ToggleButton = ({ isOpen, onClick }) => (
  <span
    style={{zIndex:"11", marginLeft:"-25px"}}
    className="toggle-button"
    onClick={onClick}
    onMouseDown={(e) => {
      // Prevent input from losing focus.
      e.preventDefault();
    }}>
    {<>
      <FontAwesomeIcon icon={faPipe} style={{zIndex:"9999", height:"20px", marginLeft:"1px"}} transform={'grow-1 down-8 left-12'} color="#cccccc" size="sm" />
      <FontAwesomeIcon icon={faChevronDown} style={{zIndex:"9999", WebkitTextStroke: '0.5px', fontWeight:"bold"}} transform={'down-8 left-4'} color="#cccccc" size="sm" />
    </>}
  </span>
);

function MapLegendControl({setShowAddressModal}) {
  return (
    <div className='leaflet-control float-end map-legend mt-2 me-2'>
      <OverlayTrigger
        key={"address-finder"}
        placement="top"
        overlay={
          <Tooltip id={`tooltip-address-finder`}>
            Search for an address to zoom the map to.
          </Tooltip>
        }
      >
        <Button onClick={() => setShowAddressModal(true)}>
          <FontAwesomeIcon icon={faSearch} />
        </Button>
      </OverlayTrigger>
    </div>
  )
}

// Zoom level used to bring the map in to a neighborhood-level view after an address search.
const NEIGHBORHOOD_ZOOM = 16;

function AnimalMapBoundsUpdater({ bounds, isAddressSearch }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !bounds) {
      return;
    }
    if (bounds.length > 1) {
      map.fitBounds(bounds);
    }
    else if (bounds.length === 1) {
      // A single point from an address search zooms in to a neighborhood-level view;
      // a single animal pin just pans over without changing the current zoom.
      isAddressSearch ? map.setView(bounds[0], NEIGHBORHOOD_ZOOM) : map.panTo(bounds[0], map.getZoom());
    }
  }, [map, bounds, isAddressSearch]);
  return null;
}

function PreRegOwnerSearch({ organization }) {

  const { state } = useContext(AuthContext);
  const { setShowSystemError } = useContext(SystemErrorContext);

	// Identify any query param data.
  const [queryParams, setQueryParams] = useQueryParams();
  const {
    search = '',
  } = queryParams;

  const fixedChoices = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
    { value: 'unknown', label: 'Unknown'}
  ];

  const sexChoices = [
    { value: 'M', label: 'Male' },
    { value: 'F', label: 'Female' },
    { value: '', label: 'Unknown' },
  ];

	const [data, setData] = useState({owners:[], animals:[],  total_count:0, isFetching:false});
  const [options, setOptions] = useState({id:null, species:[], status:[], vet_status:[], sex:[], owned:null, sheltered:null, pcolor:'', fixed:[], aggressive:[], injured:[], confined:[], aco_required:[], latlng:null, radius:null, shelter:[], building:[], room:[], public: null});
  const colorChoices = {'':[], 'dog':dogColorChoices, 'cat':catColorChoices, 'horse':horseColorChoices, 'other':otherColorChoices};
	const [searchTerm, setSearchTerm] = useState(search);
  const [showFilters, setShowFilters] = useState(false);
  const [objectType, setObjectType] = useState('owners');
  const [ownerType, setOwnerType] = useState('operational');
	const [isLoading, setIsLoading] = useState(false);
  const tempSearchTerm = useRef(null);
  const [showHidden, setShowHidden] = useState(false);
  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const [speciesChoices, setSpeciesChoices] = useState([]);
  const [colorOptions, setColorOptions] = useState([]);
  const [isDisabled, setIsDisabled] = useState(true);
	const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);
  const [goToID, setGoToID] = useState('');
  const [viewMode, setViewMode] = useState('list');
  const [mapData, setMapData] = useState({animals: [], totalCount: 0, isFetching: false});
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [mapSearchOverride, setMapSearchOverride] = useState({bounds: null, address: null});
  const handleCloseAddressModal = () => setShowAddressModal(false);
  const animalMapRef = useRef(null);
  const mapViewContainerRef = useRef(null);
  const [selectedOwners, setSelectedOwners] = useState([]);
  const [selectedAnimals, setSelectedAnimals] = useState([]);
  const {
    handleSubmitting,
    submittingComplete,
  } = useSubmitting();

  const [showOwnerHideConfirm, setShowOwnerHideConfirm] = useState(false);
  const handleOwnerHideClose = () => setShowOwnerHideConfirm(false);
  const [showAnimalHideConfirm, setShowAnimalHideConfirm] = useState(false);
  const handleAnimalHideClose = () => setShowAnimalHideConfirm(false);

  const speciesRef = useRef(null);
  const sexRef = useRef(null);
  const aggressiveRef = useRef(null);
  const injuredRef = useRef(null);
  const confinedRef = useRef(null);
  const acoRequiredRef = useRef(null);
  const fixedRef = useRef(null);
  const pcolorRef = useRef(null);

  // Update searchTerm when field input changes.
  const handleChange = event => {
    tempSearchTerm.current.value = event.target.value;
  };

  const handleIDChange = async event => {
    setGoToID(event.target.value);
  };

  // Use searchTerm to filter owners.
  const handleSubmit = async event => {
    event.preventDefault();
    setSearchTerm(tempSearchTerm.current.value);
		setPage(1);
  };

  const handleClear = () => {
    speciesRef.current.clear();
    sexRef.current.clear();
    aggressiveRef.current.clear();
    fixedRef.current.clear();
    injuredRef.current.clear();
    confinedRef.current.clear();
    acoRequiredRef.current.clear();
    pcolorRef.current.clear();
    setOptions({id:null, species:[], status:[], vet_status:[], sex:[], owned:null, sheltered:null, pcolor:[], aggressive:[], confined:[], injured:[], aco_required:[], fixed:[], shelter:[], building:[], room:[], latlng:null, radius:null, public:null});
    setTriggerRefresh(!triggerRefresh);
  };

	function setFocus(pageNum) {
    if (pageNum !== page) {
      tempSearchTerm.current.focus();
    }
  }

  const showHideOwner = async (owner_id, bool) => {
    await axios.patch('/people/api/pre-reg-person/' + owner_id + '/', {'hidden':bool})
    .then(response => {
      setData(prevState => ({ ...prevState, "owners": data.owners.filter(owner => owner.id !== response.data.id) }));
    })
    .catch(error => {
      setShowSystemError(true);
    });
  }

  const showHideAnimal = async (animal_id, bool) => {
    await axios.patch('/animals/api/prereg/' + animal_id + '/', {'hidden':bool})
    .then(response => {
      setData(prevState => ({ ...prevState, "hidden": response.data.hidden }));
    })
    .catch(error => {
      setShowSystemError(true);
    });
  }

  const handleHideOwnerSubmit = async (bool) => {
    await axios.patch('/people/api/pre-reg-person/mass_show_hide/', {'ids':selectedOwners, 'hidden':bool})
    .then(response => {
      setData(prevState => ({ ...prevState, "owners": data.owners.filter(owner => !selectedOwners.includes(owner.id)) }))
      setSelectedOwners([]);
      handleOwnerHideClose();
    })
    .catch(error => {
      setShowSystemError(true);
    });
  }

  const handleHideAnimalSubmit = async (bool) => {
    await axios.patch('/animals/api/prereg/mass_show_hide/', {'ids':selectedAnimals, 'hidden':bool})
    .then(response => {
      setData(prevState => ({ ...prevState, "animals": data.animals.filter(animal => !selectedAnimals.includes(animal.id)) }))
      setSelectedAnimals([]);
      handleAnimalHideClose();
    })
    .catch(error => {
      setShowSystemError(true);
    });
  }

  const handleGeoJsonDownload = (e) => {
    setIsLoading(true);

    handleSubmitting()
    .then(() => downloadGeoJson()
      )
    .then(submittingComplete)
    .finally(() => setIsLoading(false));
  }

  // Walks every page of matching pre-reg animals, bypassing pagination. Used by the GeoJSON
  // download button, which needs the full (non-map) serializer payload.
  const fetchAllPreRegAnimals = async ({ cancelToken, onChunk } = {}) => {
    let animals = [];
    let nextUrl = '/animals/api/prereg/?page=1&search=' + searchTerm + '&organization=' + organization + '&hidden=' + showHidden;
    let isFirstPage = true;
    do {
      const response = await axios.get(nextUrl, {
        cancelToken,
        params: isFirstPage ? {
          species: options.species,
          sex: options.sex,
          fixed: options.fixed,
          aggressive: options.aggressive,
          aco_required: options.aco_required,
          injured: options.injured,
          confined: options.confined,
          pcolor: options.pcolor,
        } : undefined,
      })
      .catch(error => {
        if (error.response) {
          setShowSystemError(true);
        }
        return null;
      });

      if (!response) {
        break;
      }

      isFirstPage = false;
      animals.push(...response.data.results);
      if (onChunk) {
        onChunk(response.data.results, response.data.count);
      }

      nextUrl = response.data.next;
      if (nextUrl) {
        nextUrl = '/animals/' + nextUrl.split('/animals/')[1];
      }
    } while(nextUrl != null)

    return animals;
  };

  // Fetches every matching pre-reg animal for the map in one shot: the map serializer is small
  // enough (see MapPreRegAnimalSerializer) that the backend skips pagination entirely for
  // `?no_page=true` requests.
  const fetchMapPreRegAnimals = async ({ cancelToken } = {}) => {
    const response = await axios.get('/animals/api/prereg/?map=true&no_page=true&search=' + searchTerm + '&organization=' + organization + '&hidden=' + showHidden, {
      cancelToken,
      params: {
        species: options.species,
        sex: options.sex,
        fixed: options.fixed,
        aggressive: options.aggressive,
        aco_required: options.aco_required,
        injured: options.injured,
        confined: options.confined,
        pcolor: options.pcolor,
      },
    })
    .catch(error => {
      if (error.response) {
        setShowSystemError(true);
      }
      return null;
    });

    return response ? response.data : [];
  };

  const handleAnimalGeoJsonDownload = (e) => {
    setIsLoading(true);

    handleSubmitting()
    .then(() => downloadAnimalGeoJson()
      )
    .then(submittingComplete)
    .finally(() => setIsLoading(false));
  }

  const downloadAnimalGeoJson = async () => {
    const pre_reg_animals = await fetchAllPreRegAnimals();

    var params = '';
    pre_reg_animals.forEach(animal => params = params + "id=" + animal.id + "&")
    var fileDownload = require('js-file-download');
    axios.get('/people/api/pre-reg-person/download_all/', {
            params: {
              ids: params
            },
            responseType: 'blob',
        }).then(res => {
            fileDownload(res.data, `Animals-${moment().format(DATE_FORMAT)}` + '.geojson');
        }).catch(err => {
        })
  }

  const downloadCsv = () => {
    const headers = [
      'ID',
      'Name',
      'Count',
      'Species',
      'Status',
      'Sex',
      'Age',
      'Size',
      'Primary Color',
      'Secondary Color',
      'Microchip',
      'External ID',
      'Fixed',
      'Injured',
      'Confined',
      'Aggressive',
      'Special Handling',
      'Service Request',
      'Shelter',
      'Building',
      'Room',
    ];

    const rows = data.animals.map(item => {
      return [
        item.name || "Unknown Name",
        item.species_string,
        item.animal_count,
        item.sex||"N/A",
        item.age||"N/A",
        item.size||"N/A",
        item.pcolor||"N/A",
        item.scolor||"N/A",
        item.microchip||"N/A",
        item.external_id||"N/A",
        item.fixed||"N/A",
        item.injured||"N/A",
        item.confined||"N/A",
        item.aggressive||"N/A",
        item.aco_required||"N/A",
        item.request_address||"N/A",
        item.shelter_object?.name||"N/A",
        item.building_name||"N/A",
        item.room_name||"N/A",
      ];
    });

    const csv = [
      headers.join(','),
      ...rows.map(row =>
        row.map(value =>
          `"${String(value ?? '').replace(/"/g, '""')}"`
        ).join(',')
      ),
    ].join('\n');

    const blob = new Blob([csv], {
      type: 'text/csv;charset=utf-8;',
    });

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `${organization}-animal-list.csv`;
    link.click();

    URL.revokeObjectURL(link.href);
  };

  const downloadGeoJson = async () => {
    // Fetch all pre-reg person data.
    let pre_reg_persons = [];
    let nextUrl = '/people/api/pre-reg-person/?light=true&page=1&search=' + searchTerm + '&owner_type=' + ownerType + '&organization=' + organization + '&hidden=' + showHidden;
    do {
      const response = await axios.get(nextUrl)
      .catch(error => {
        if (error.response) {
          setShowSystemError(true);
        }
      });

      pre_reg_persons.push(...response.data.results);
      nextUrl = response.data.next;
      if (nextUrl) {
        nextUrl = '/people/' + response.data.next.split('/people/')[1];
      }
    } while(nextUrl != null)

    var params = '';
    pre_reg_persons.forEach(person => params = params + "id=" + person.id + "&")
    var fileDownload = require('js-file-download');
    axios.get('/people/api/pre-reg-person/download_all/', { 
            params: {
              ids: params
            },
            responseType: 'blob',
        }).then(res => {
            fileDownload(res.data, `Persons-${moment().format(DATE_FORMAT)}` + '.geojson');
        }).catch(err => {
        })
  }

  // Hook for initializing data.
  useEffect(() => {
    let unmounted = false;
    let source = axios.CancelToken.source();

    tempSearchTerm.current.focus();

    const fetchSpecies = () => {
      setSpeciesChoices([]);
      // Fetch Species data.
      axios.get('/animals/api/species/', {
        cancelToken: source.token,
      })
      .then(response => {
        if (!unmounted) {
          let species_options = [];
          response.data.forEach(result => {
            // Build species option list.
            species_options.push({value: result.name, label: result.name});
          });
          setSpeciesChoices(species_options);
        }
      })
      .catch(error => {
        if (!unmounted) {
          setShowSystemError(true);
        }
      });
    };
    fetchSpecies();

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, []);

	// Hook for initializing data.
	useEffect(() => {
		let unmounted = false;
		let source = axios.CancelToken.source();
    setNumPages(0);

		const fetchOwners = () => {
			setData({animals:[], owners:[], total_count:0, isFetching:true});
			// Fetch People data.
			axios.get('/people/api/pre-reg-person/?light=true&page=' + page + '&search=' + searchTerm + '&owner_type=' + ownerType + '&organization=' + organization + '&hidden=' + showHidden, {
				cancelToken: source.token,
			})
			.then(response => {
				if (!unmounted) {
					setNumPages(Math.ceil(response.data.count / ITEMS_PER_PAGE));
					setData({owners:response.data.results, animals:[], total_count:response.data.count, isFetching:false});
				}
			})
			.catch(error => {
				if (!unmounted) {
					setData({owners: [], isFetching: false});
          setShowSystemError(true);
				}
			});
		};
    if (objectType === 'owners') {
      fetchOwners();
    }

    const fetchAnimals = () => {
			setData({animals:[], owners:[], total_count:0, isFetching:true});
			// Fetch People data.
			axios.get('/animals/api/prereg/?page=' + page + '&search=' + searchTerm + '&organization=' + organization + '&hidden=' + showHidden, {
				cancelToken: source.token,
        params: {
          species: options.species,
          sex: options.sex,
          fixed: options.fixed,
          aggressive: options.aggressive,
          aco_required: options.aco_required,
          injured: options.injured,
          confined: options.confined,
          pcolor: options.pcolor,
        },
			})
			.then(response => {
				if (!unmounted) {
					setNumPages(Math.ceil(response.data.count / ITEMS_PER_PAGE));
					setData({owners:[], animals:response.data.results, total_count:response.data.count, isFetching:false});
				}
			})
			.catch(error => {
				if (!unmounted) {
					setData({owners:[], animals:[], isFetching:false});
          setShowSystemError(true);
				}
			});
		};
    if (objectType === 'animals') {
      fetchAnimals();
    }

		// Cleanup.
		return () => {
			unmounted = true;
			source.cancel();
		};
	}, [searchTerm, page, ownerType, objectType, showHidden, triggerRefresh]);

  // Hook for progressively loading every matching pre-reg animal in the background, so the
  // map's pins are already loaded by the time someone switches over to Map view.
  useEffect(() => {
    if (objectType !== 'animals') {
      return;
    }

    let unmounted = false;
    let source = axios.CancelToken.source();

    const loadMapData = async () => {
      setMapData({animals: [], totalCount: 0, isFetching: true});
      setMapSearchOverride({bounds: null, address: null});

      const animals = await fetchMapPreRegAnimals({ cancelToken: source.token });

      if (!unmounted) {
        setMapData({animals, totalCount: animals.length, isFetching: false});
      }
    };
    loadMapData();

    // Cleanup.
    return () => {
      unmounted = true;
      source.cancel();
    };
  }, [searchTerm, objectType, showHidden, triggerRefresh]);

  // Hook handling color options when species changes.
  useEffect(() => {
    let color_choices = [];
    if (options.species.includes('dog')) {
      color_choices = color_choices.concat(colorChoices['dog']);
    }
    if (options.species.includes('cat')) {
      color_choices = color_choices.concat(colorChoices['cat']);
    }
    if (options.species.includes('horse')) {
      color_choices = color_choices.concat(colorChoices['horse']);
    }
    if (options.species.filter(specie => !['dog', 'cat', 'horse'].includes(specie)).length > 0) {
      color_choices = color_choices.concat(colorChoices['other']);
    }
    // Dedupe and sort.
    setColorOptions(color_choices.filter((v, i, a) => a.findIndex(t => t.value === v.value) === i).sort(function(a, b) {
      return a['label'].localeCompare(b['label']);
    }));
  }, [options.species]);

  // Hook handling option changes.
  useEffect(() => {
    setIsDisabled(
      !(
        options.id ||
        options.species ||
        options.status ||
        options.vet_status ||
        options.sex ||
        options.pcolor ||
        options.aggressive ||
        options.fixed ||
        options.injured ||
        options.confined ||
        options.aco_required ||
        options.latlng ||
        options.shelter ||
        options.building ||
        options.room ||
        options.owned ||
        options.sheltered ||
        options.public !== null
      )
    );
  }, [options]);

  // Pre-reg animals only ever have their registered (home) address, so there's no location-type choice here.
  const getAnimalLatLon = (animal) => {
    if (!animal.latitude || !animal.longitude) {
      return null;
    }
    return [parseFloat(animal.latitude), parseFloat(animal.longitude)];
  };

  const getAnimalAddress = (animal) => animal.home_location;

  const animalMarkerIcon = useMemo(() => new L.DivIcon({
    html: ReactDOMServer.renderToString(<FontAwesomeIcon color="#ff4c4c" size="lg" className="icon-border" icon={faMapMarkerAlt} />),
    iconSize: [0, 0],
    iconAnchor: [5, 10],
    className: "pin-icon",
    popupAnchor: null,
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null
  }), []);

  const searchMarkerIcon = useMemo(() => new L.DivIcon({
    html: ReactDOMServer.renderToString(<FontAwesomeIcon color="#2f8fff" size="lg" className="icon-border" icon={faMapMarkerAlt} />),
    iconSize: [0, 0],
    iconAnchor: [5, 10],
    className: "pin-icon",
    popupAnchor: null,
    shadowUrl: null,
    shadowSize: null,
    shadowAnchor: null
  }), []);

  // Bounds for the map view. An address search overrides this until it's reset or new data loads.
  // There's no incident to fall back to here, so with no mapped animals there's simply no map to show.
  const mapViewBounds = useMemo(() => {
    if (mapSearchOverride.bounds) {
      return mapSearchOverride.bounds;
    }
    const animalBounds = mapData.animals.map(getAnimalLatLon).filter(latlon => latlon !== null);
    return animalBounds.length > 0 ? animalBounds : null;
  }, [mapData.animals, mapSearchOverride]);

  // How many of the matching animals don't have a mappable address.
  const animalsMissingLocation = mapData.animals.filter(animal => getAnimalLatLon(animal) === null).length;

  // Let the map fill the rest of the viewport below it instead of a fixed height.
  const mapViewHeight = useFillRemainingHeight(mapViewContainerRef, { minHeight: 400, deps: [viewMode, showFilters] });

  // Leaflet only re-measures its container on window resize, not when we resize the
  // container ourselves (e.g. toggling the filters panel), so nudge it explicitly.
  useEffect(() => {
    if (objectType === 'animals' && viewMode === 'map' && animalMapRef.current) {
      animalMapRef.current.invalidateSize();
    }
  }, [mapViewHeight, viewMode, objectType]);

	return (
    <div className="ms-2 me-2">
    <Header>
      <span id="back" style={{cursor:'pointer'}} onClick={(e) => {navigate("/" + organization + "/manage/organization")}} className="me-3"><FontAwesomeIcon icon={faArrowAltCircleLeft} size="lg" inverse /></span>
      Manage Preregistration
    </Header>
    <Row className="ms-0 mb-3" style={{marginRight:"9px"}}>
      <Col xs="6" className="ps-0 pe-0">
        <ButtonGroup className="w-100 border rounded" style={{height:"49px"}}>
          <Button
            variant={objectType === "owners" ? "primary" : "secondary"}
            onClick={() => {
              setPage(1);
              setSelectedOwners([]);
              setSelectedAnimals([]);
              setShowFilters(false);
              setObjectType('owners');
            }}
            size="lg"
          >
            Owners
          </Button>
          <Button
            variant={objectType === "animals" ? "primary" : "secondary"}
            onClick={() => {
              setPage(1);
              setSelectedOwners([]);
              setSelectedAnimals([]);
              setObjectType('animals');
            }}
            size="lg"
          >
            Animals
          </Button>
        </ButtonGroup>
            </Col>
          </Row>
    <Form onSubmit={handleSubmit}>
      <Row style={{marginRight:"15px"}}>
        <Col xs="2" style={{maxWidth:"150px", marginRight:"-10px", paddingRight:"0px"}}>
          <InputGroup>
            <FormControl
              type="text"
              placeholder="ID #"
              name="searchIDTerm"
              onChange={handleIDChange}
            />
             {objectType === 'owners' ? <Button variant="outline-light" type="submit" disabled={!goToID} style={{borderRadius:"0 5px 5px 0", color:"white"}} onClick={(e) => {navigate("/" + organization + "/manage/owners/" + goToID)}}>Go</Button> :
                                        <Button variant="outline-light" type="submit" disabled={!goToID} style={{borderRadius:"0 5px 5px 0", color:"white"}} onClick={(e) => {navigate("/" + organization + "/manage/animals/" + goToID)}}>Go</Button>}
          </InputGroup>
        </Col>
        <Col>
          <InputGroup>
            <FormControl
              type="text"
              placeholder="Search"
              name="searchTerm"
              onChange={handleChange}
              ref={tempSearchTerm}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSubmit(e);
                }
              }}
              />
              <Button variant="outline-light" type="submit" style={{borderRadius:"0 5px 5px 0", color:"white"}}>Search
                <OverlayTrigger
                  key={"search-information"}
                  placement="right"
                  overlay={
                    <Tooltip id={`tooltip-search-information`}>
                      {objectType === 'owners' ? 'Searchable fields: name, phone number, email, drivers license, address fields, evacuation zone, and animal names.' : 'Searchable fields: name, microchip, external ID, address fields, and owner last names.'}
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faInfoCircle} className="ms-1 fa-move-up" size="sm" inverse />
                </OverlayTrigger>
              </Button>
              <ButtonGroup className="ms-1 me-1 border rounded" style={{height:"38px"}}>
                <Button
                  variant={showHidden === false ? "primary" : "secondary"}
                  onClick={() => {
                    setPage(1);
                    setSelectedOwners([]);
                    setSelectedAnimals([]);
                    setShowHidden(false);
                  }}
                >
                  Discoverable
                </Button>
                <Button
                  variant={showHidden === true ? "primary" : "secondary"}
                  onClick={() => {
                    setPage(1);
                    setSelectedOwners([]);
                    setSelectedAnimals([]);
                    setShowHidden(true);
                  }}
                >
                  Hidden
                </Button>
              </ButtonGroup>
              {objectType === 'animals' ? <Button variant="outline-light" className="me-1 rounded" style={{color:"white"}} onClick={() => {setShowFilters(!showFilters)}}>Advanced {showFilters ? <FontAwesomeIcon icon={faChevronDoubleUp} className="fa-move-up" size="sm" /> : <FontAwesomeIcon icon={faChevronDoubleDown} className="fa-move-up" size="sm" />}</Button> : ""}
              {objectType === 'owners' ? <ButtonGroup className="me-1 border rounded" style={{height:"38px"}}>
                <Button
                  variant={ownerType === "operational" ? "primary" : "secondary"}
                  onClick={() => {
                    setPage(1);
                    setSelectedOwners([]);
                    setSelectedAnimals([]);
                    setOwnerType('operational');
                  }}
                >
                  Operational
                </Button>
                <Button
                  variant={ownerType === "training" ? "primary" : "secondary"}
                  onClick={() => {
                    setPage(1);
                    setSelectedOwners([]);
                    setSelectedAnimals([]);
                    setOwnerType('training');
                  }}
                >
                  Training
                </Button>
              </ButtonGroup> : ""}
              {objectType === 'owners' ? <ActionsDropdown variant="outline-light" title={"Actions"} search={true}>
                <LoadingLink
                  href={"/" + organization + "/manage/owners/new"}
                  className="d-block"
                  isLoading={false}
                  label="Add a New Preregistered Owner"
                  >
                    <FontAwesomeIcon icon={faPlusSquare} className="fas" inverse />
                </LoadingLink>
                <LoadingLink
                  onClick={() => {setShowOwnerHideConfirm(true);}}
                  className="d-block"
                  isLoading={false}
                  label={showHidden ? "Make Selected Owners Discoverable" : "Hide Selected Owners From Discovery"}
                  disabled={selectedOwners.length === 0}
                  disabled_text="Must have owners selected."
                  >
                  <FontAwesomeIcon icon={showHidden ? faEye : faEyeSlash} className="fas" inverse />
                </LoadingLink>
                <LoadingLink onClick={handleGeoJsonDownload} disabled={data.isFetching} isLoading={data.isFetching} label="Download Preregistered Owners as GeoJSON" className="d-block">
                  <FontAwesomeIcon icon={faFileBracketsCurly} inverse />
                </LoadingLink>
              </ActionsDropdown> :
              <ActionsDropdown variant="outline-light" title={"Actions"} search={true}>
                <LoadingLink
                  onClick={() => {setShowAnimalHideConfirm(true);}}
                  className="d-block"
                  isLoading={false}
                  label={showHidden ? "Make Selected Animals Discoverable" : "Hide Selected Animals From Discovery"}
                  disabled={selectedAnimals.length === 0}
                  disabled_text="Must have animals selected."
                  >
                  <FontAwesomeIcon icon={showHidden ? faEye : faEyeSlash} className="fas" inverse />
                </LoadingLink>
                <LoadingLink onClick={() => downloadCsv()} label="Download Preregistered Animal List" disabled={data.isFetching} isLoading={data.isFetching} className="d-block">
                  <FontAwesomeIcon icon={faFileCsv} className="fas" style={{cursor:'pointer'}} inverse />
                </LoadingLink>
                <LoadingLink onClick={handleGeoJsonDownload} disabled={data.isFetching} isLoading={data.isFetching} label="Download Preregistered Animals as GeoJSON" className="d-block">
                  <FontAwesomeIcon icon={faFileBracketsCurly} inverse />
                </LoadingLink>
              </ActionsDropdown>}
              {objectType === 'animals' ? <ButtonGroup className="ms-1" aria-label="Toggle list or map view">
                <Button variant={viewMode === 'list' ? 'primary' : 'outline-light'} style={{color:"white"}} onClick={() => setViewMode('list')}>
                  <FontAwesomeIcon icon={faList} className="me-1" />List
                </Button>
                <Button variant={viewMode === 'map' ? 'primary' : 'outline-light'} style={{color:"white"}} onClick={() => setViewMode('map')}>
                  <FontAwesomeIcon icon={faMapMarkedAlt} className="me-1" />Map
                </Button>
              </ButtonGroup> : ""}
          </InputGroup>
        </Col>
      </Row>
      <Collapse in={showFilters}>
          <div>
          <Card className="border rounded d-flex mt-3" style={{width:"100%"}}>
            <Card.Body style={{marginBottom:"-16px"}}>
              <Row className="gx-2">
                <Col xs={"9"} style={{textTransform:"capitalize"}}>
                  <Typeahead
                    id="species"
                    name="species"
                    multiple
                    onChange={(values) => {
                      pcolorRef.current.clear();
                      setOptions({...options, species: values.map(option => option.value), pcolor: []});
                    }}
                    ref={speciesRef}
                    options={speciesChoices}
                    placeholder="Filter by Species..."
                    className="species-typeahead mb-2 no-wrap-container w-100"
                    selected={speciesChoices.filter(choice => options.species.includes(choice.value))}
                    inputProps={{ className: 'custom-typeahead-input' }}
                  >
                    {({ isMenuShown, toggleMenu }) => (
                      <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                    )}
                  </Typeahead>
                  <OverlayTrigger
                    key={"disabled"}
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-disabled`} hidden={options.species.length > 0} style={{marginBottom:"-15px"}}>
                        {options.species.length === 0 ? "One or more species must be selected to pick a color." : ""}
                      </Tooltip>
                    }
                  >
                    <span>
                      <Typeahead
                        id="pcolor"
                        name="pcolor"
                        multiple
                        onChange={(values) => {
                          setOptions({...options, pcolor: values.map(option => option.value)});
                        }}
                        ref={pcolorRef}
                        disabled={options.species.length === 0}
                        options={colorOptions}
                        placeholder="Filter by Primary Color..."
                        className="pcolor-typeahead mb-2 no-wrap-container w-100"
                        inputProps={{ className: 'custom-typeahead-input' }}
                      >
                        {({ isMenuShown, toggleMenu }) => (
                          <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                        )}
                      </Typeahead>
                    </span>
                  </OverlayTrigger>
                </Col>
                <Col className="flex-grow-1" xs="3">
                  <Button className="btn btn-primary" style={{maxHeight:"35px", width:"100%"}} onClick={() => {tempSearchTerm.current.value !== searchTerm ? setSearchTerm(tempSearchTerm.current.value) : setTriggerRefresh(!triggerRefresh);}} disabled={isDisabled}>Apply</Button>
                  <Button variant="outline-light" style={{maxHeight:"35px", width:"100%", marginTop:"9px", marginBottom:"10px"}} onClick={handleClear}>Clear</Button>
                </Col>
              </Row>
              <Row className="gx-2">
                <Col xs={"3"} style={{textTransform:"capitalize"}}>
                  <Typeahead
                    id="sex"
                    name="sex"
                    multiple
                    onChange={(values) => {
                      setOptions({...options, sex: values.map(option => option.value)});
                    }}
                    ref={sexRef}
                    options={sexChoices}
                    placeholder="Filter by Sex..."
                    className="sex-typeahead mb-2 no-wrap-container w-100"
                    selected={sexChoices.filter(choice => options.sex.includes(choice.value))}
                    inputProps={{ className: 'custom-typeahead-input' }}
                  >
                    {({ isMenuShown, toggleMenu }) => (
                      <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                    )}
                  </Typeahead>
                </Col>
                <Col xs={"3"}>
                  <Typeahead
                    id="aggressive"
                    name="aggressive"
                    multiple
                    onChange={(values) => {
                      setOptions({...options, aggressive: values.map(option => option.value)});
                    }}
                    ref={aggressiveRef}
                    options={fixedChoices}
                    placeholder="Filter by Aggressive..."
                    className="aggressive-typeahead mb-2 no-wrap-container w-100"
                    selected={fixedChoices.filter(choice => options.aggressive.includes(choice.value))}
                    inputProps={{ className: 'custom-typeahead-input' }}
                  >
                    {({ isMenuShown, toggleMenu }) => (
                      <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                    )}
                  </Typeahead>
                </Col>
                <Col xs={"3"}>
                  <Typeahead
                    id="aco_required"
                    name="aco_required"
                    multiple
                    onChange={(values) => {
                      setOptions({...options, aco_required: values.map(option => option.value)});
                    }}
                    ref={acoRequiredRef}
                    options={fixedChoices}
                    placeholder="Filter by Special Handling..."
                    className="aco_required-typeahead mb-2 no-wrap-container w-100"
                    selected={fixedChoices.filter(choice => options.aco_required.includes(choice.value))}
                    inputProps={{ className: 'custom-typeahead-input' }}
                  >
                    {({ isMenuShown, toggleMenu }) => (
                      <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                    )}
                  </Typeahead>
                </Col>
              </Row>
               <Row className="gx-2">
                <Col xs={"3"} style={{textTransform:"capitalize"}}>
                  <Typeahead
                    id="fixed"
                    name="fixed"
                    multiple
                    onChange={(values) => {
                      setOptions({...options, fixed: values.map(option => option.value)});
                    }}
                    ref={fixedRef}
                    options={fixedChoices}
                    placeholder="Filter by Altered..."
                    className="fixed-typeahead mb-2 no-wrap-container w-100"
                    selected={fixedChoices.filter(choice => options.fixed.includes(choice.value))}
                    inputProps={{ className: 'custom-typeahead-input' }}
                  >
                    {({ isMenuShown, toggleMenu }) => (
                      <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                    )}
                  </Typeahead>
                </Col>
                <Col xs={"3"}>
                  <Typeahead
                    id="injured"
                    name="injured"
                    multiple
                    onChange={(values) => {
                      setOptions({...options, injured: values.map(option => option.value)});
                    }}
                    ref={injuredRef}
                    options={fixedChoices}
                    placeholder="Filter by Injured..."
                    className="injured-typeahead mb-2 no-wrap-container w-100"
                    selected={fixedChoices.filter(choice => options.injured.includes(choice.value))}
                    inputProps={{ className: 'custom-typeahead-input' }}
                  >
                    {({ isMenuShown, toggleMenu }) => (
                      <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                    )}
                  </Typeahead>
                </Col>
                <Col xs={"3"}>
                  <Typeahead
                    id="confined"
                    name="confined"
                    multiple
                    onChange={(values) => {
                      setOptions({...options, confined: values.map(option => option.value)});
                    }}
                    ref={confinedRef}
                    options={fixedChoices}
                    placeholder="Filter by Confined..."
                    className="confined-typeahead mb-2 no-wrap-container w-100"
                    selected={fixedChoices.filter(choice => options.confined.includes(choice.value))}
                    inputProps={{ className: 'custom-typeahead-input' }}
                  >
                    {({ isMenuShown, toggleMenu }) => (
                      <ToggleButton isOpen={isMenuShown} onClick={(e) => toggleMenu()} />
                    )}
                  </Typeahead>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          </div>
        </Collapse>
    </Form>
    {objectType === "owners" ? <Row className="flex-nowrap mt-3">
      <Col style={{minWidth:"50px", maxWidth:"50px", marginLeft:"1px"}}>
          Select
      </Col>
      <Col style={{minWidth:"150px", maxWidth:"150px", marginLeft:"1px"}}>
          Last Name
      </Col>
      <Col style={{minWidth:"150px", maxWidth:"150px", marginLeft:"1px"}}>
          First Name
      </Col>
      <Col style={{minWidth:"350px", maxWidth:"350px"}}>
          Address
      </Col>
      <Col style={{minWidth:"150px", maxWidth:"150px"}}>
          Phone
      </Col>
      <Col style={{minWidth:"330px", maxWidth:"330px"}}>
          Email
      </Col>
      <Col style={{minWidth:"105px", maxWidth:"105px"}} className="pe-0">
        Animal Count
      </Col>
      <Col style={{minWidth:"100px", maxWidth:"100px"}}>
          Actions
      </Col>
    </Row> :
    viewMode === 'list' ?
    <Row className="flex-nowrap mt-3">
      <Col style={{minWidth:"50px", maxWidth:"50px", marginLeft:"1px"}}>
        Select
      </Col>
      <Col style={{minWidth:"150px", maxWidth:"150px", marginLeft:"0px"}}>
        Name
      </Col>
      <Col style={{minWidth:"150px", maxWidth:"150px", marginLeft:"1px"}}>
        Species
      </Col>
      <Col style={{minWidth:"45px", maxWidth:"45px"}} className="pe-0">
        Count
      </Col>
      <Col style={{minWidth:"35px", maxWidth:"35px"}}>
        Sex
      </Col>
      <Col style={{minWidth:"350px", maxWidth:"350px"}}>
        Address
      </Col>
      <Col style={{minWidth:"300px", maxWidth:"300px"}}>
        Owner Names
      </Col>
      <Col style={{minWidth:"100px", maxWidth:"100px"}}>
        Microchip
      </Col>
      <Col style={{minWidth:"100px", maxWidth:"100px"}}>
        External ID
      </Col>
      <Col style={{minWidth:"100px", maxWidth:"100px"}}>
        Actions
      </Col>
    </Row> : ""}
    {data.owners.map(owner => (
      <Card key={owner.id} className="rounded mb-1" style={{height:"32px", marginLeft:"12px", maxWidth:"741px"}}>
        <div className="row no-gutters flex-nowrap me-0">
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", borderTopLeftRadius:"0.25rem", borderBottomLeftRadius:"0.25rem", minWidth:"50px", maxWidth:"50px", backgroundColor:"#615e5e"}}>
            <Checkbox
              id={'select' + owner.id}
              name={'select' + owner.id}
              style={{
                transform: "scale(.75)",
                marginRight: "1px",
                marginTop: "-6px",
                marginBottom: "-5px",
                paddingLeft:"13px"
              }}
              onChange={() => {
                if (selectedOwners.includes(owner.id)) {
                  setSelectedOwners(selectedOwners.filter(owner_id => owner_id !== owner.id));
                }
                else {
                  setSelectedOwners(selectedOwners.concat(owner.id));
                }
              }}
            />
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"150px", maxWidth:"150px", backgroundColor:"#615e5e"}} title={owner.last_name}>
            {owner.last_name}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"150px", maxWidth:"150px", backgroundColor:"#615e5e"}} title={owner.first_name}>
            {owner.first_name}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"350px", maxWidth:"350px", backgroundColor:"#615e5e"}} title={owner.full_address}>
            {owner.full_address}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"150px", maxWidth:"150px", backgroundColor:"#615e5e"}} title={owner.display_phone}>
            {owner.display_phone}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"330px", maxWidth:"330px", backgroundColor:"#615e5e"}} title={owner.email}>
            {owner.email ?? 'N/A'}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"105px", maxWidth:"105px", backgroundColor:"#615e5e"}}>
            {owner.pre_reg_animal_count}
          </Col>
          <Col className="border" style={{height:"32px", paddingRight:"3px", paddingTop:"2px", marginTop: "-1px", fontSize:"13px", minWidth:"100px", maxWidth:"100px", borderTopRightRadius:"0.25rem", borderBottomRightRadius:"0.25rem", backgroundColor:"#615e5e"}}>
						<ActionsDropdown variant="secondary" title={"Actions"} disabled={selectedOwners.length > 0} className="float-end" style={{height:"26px", paddingTop:"0px"}}>
							<LoadingLink
								href={"/" + organization + "/manage/owners/" + owner.id}
								className="d-block"
								isLoading={false}
								label="Owner Details"
								>
								  <FontAwesomeIcon icon={faUserAlt} className="fas" inverse />
							</LoadingLink>
              <LoadingLink
                  href={"/" + organization + "/manage/owners/edit/" + owner.id}
                  isLoading={false}
                  className="d-block"
                  label="Update Owner"
                >
                  <FontAwesomeIcon icon={faEdit} className="fas" inverse />
                </LoadingLink>
                {owner.hidden ?
                  <LoadingLink
                    onClick={() => showHideOwner(owner.id, false)}
                    className="d-block"
                    isLoading={false}
                    label="Make Owner Discoverable"
                    >
                    <FontAwesomeIcon icon={faEye} className="fas" inverse />
                  </LoadingLink>
                  :
                  <LoadingLink
                    onClick={() => showHideOwner(owner.id, true)}
                    className="d-block"
                    isLoading={false}
                    label="Hide Owner From Discovery"
                    >
                    <FontAwesomeIcon icon={faEyeSlash} className="fas" inverse />
                  </LoadingLink>
                  }
						</ActionsDropdown>
          </Col>
        </div>
      </Card>
    ))}
    {objectType === 'animals' && viewMode === 'map' ? (
      <div ref={mapViewContainerRef} className="mt-3 border rounded me-4" style={{overflow:"hidden", '--map-view-height': mapViewHeight + 'px'}}>
        {/* {mapData.isFetching ?
        <p className="mb-0 p-2">Fetching Animals...</p>
        : animalsMissingLocation > 0 ?
        <p className="mb-0 p-2">
          {animalsMissingLocation} of {mapData.animals.length} matching animals {animalsMissingLocation === 1 ? "doesn't" : "don't"} have a registered address and {animalsMissingLocation === 1 ? "isn't" : "aren't"} shown below.
        </p>
        : ""} */}
        {mapViewBounds ?
        <Map ref={animalMapRef} bounds={mapViewBounds.length > 1 ? mapViewBounds : null} center={mapViewBounds.length === 1 ? mapViewBounds[0] : null} boundsOptions={{padding:[20,20]}} className="prereg-animal-search-map-view-leaflet-container">
          <MapLegendControl setShowAddressModal={setShowAddressModal} />
          <AnimalMapBoundsUpdater bounds={mapViewBounds} isAddressSearch={!!mapSearchOverride.bounds} />
          {mapSearchOverride.bounds && mapSearchOverride.bounds.length === 1 ?
            <Marker position={mapSearchOverride.bounds[0]} icon={searchMarkerIcon}>
              <MapTooltip direction="top">Search Location: {mapSearchOverride.address || 'Unknown'}</MapTooltip>
            </Marker>
          : ""}
          {mapData.animals.map((animal) => {
            const latlon = getAnimalLatLon(animal);
            if (!latlon) {
              return null;
            }
            return (
              <Marker
                key={animal.id}
                position={latlon}
                icon={animalMarkerIcon}
                eventHandlers={{
                  click() {
                    navigate("/" + organization + "/manage/animals/" + animal.id);
                  },
                }}
              >
                <MapTooltip direction="top">
                  <div>
                    <b>{animal.animal_count > 1 ? `${animal.animal_count} ${titleCase(animal.species_string)}` : (animal.name ? titleCase(animal.name) : "Unknown Name")}</b><br />
                    Species: {titleCase(animal.species_string_singular)}<br />
                    Sex: {animal.sex === 'M' ? 'Male' : animal.sex === 'F' ? 'Female' : 'Unknown'}<br />
                    Address: {getAnimalAddress(animal) || 'Unknown'}
                  </div>
                </MapTooltip>
              </Marker>
            );
          })}
        </Map>
        : <p className="mb-0 p-2">No mapped addresses to display.</p>}
        {mapData.isFetching ?
          <p className="mb-0 p-2">Fetching Animals...</p>
        : mapData.animals.length - animalsMissingLocation === 0 ?
          <p className="mb-0 p-2">
            None of the {mapData.animals.length} matching animals have a registered address.
          </p>
        : mapData.animals.length - animalsMissingLocation === mapData.animals.length ?
          <p>All of the {mapData.animals.length} matching animals have a registered address and are displayed above.</p>
        : animalsMissingLocation > 0 ?
          <p className="mb-0 p-2">
            {mapData.animals.length - animalsMissingLocation} of {mapData.animals.length} matching animals {mapData.animals.length - animalsMissingLocation === 1 ? "has" : "have"} a registered address and {mapData.animals.length - animalsMissingLocation === 1 ? "is" : "are"} displayed above.
          </p>
        :""}
      </div>
    ) : ""}
    {/* # Name, Species, Sex, Animal Count, Address, Owner Name, Owner Phone */}
    {objectType === 'animals' && viewMode === 'list' ? data.animals.map(animal => (
      <Card key={animal.id} className="rounded mb-1" style={{height:"32px", marginLeft:"12px", maxWidth:"741px"}}>
        <div className="row no-gutters flex-nowrap me-0">
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", borderTopLeftRadius:"0.25rem", borderBottomLeftRadius:"0.25rem", minWidth:"50px", maxWidth:"50px", backgroundColor:"#615e5e"}}>
            <Checkbox
              id={'select' + animal.id}
              name={'select' + animal.id}
              style={{
                transform: "scale(.75)",
                marginRight: "1px",
                marginTop: "-6px",
                marginBottom: "-5px",
                paddingLeft:"13px"
              }}
              onChange={() => {
                if (selectedAnimals.includes(animal.id)) {
                  setSelectedAnimals(selectedAnimals.filter(animal_id => animal_id !== animal.id));
                }
                else {
                  setSelectedAnimals(selectedAnimals.concat(animal.id));
                }
              }}
            />
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"150px", maxWidth:"150px", backgroundColor:"#615e5e"}} title={animal.name || "Unknown Name"}>
            {animal.name || "Unknown Name"}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"150px", maxWidth:"150px", backgroundColor:"#615e5e"}} title={animal.species_string_singular}>
            {capitalize(animal.species_string_singular)}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"45px", maxWidth:"45px", backgroundColor:"#615e5e"}} title={animal.animal_count}>
            {animal.animal_count}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"35px", maxWidth:"35px", backgroundColor:"#615e5e"}} title={animal.sex}>
            {animal.sex}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"350px", maxWidth:"350px", backgroundColor:"#615e5e"}} title={animal.found_location}>
            {animal.home_location}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"300px", maxWidth:"300px", backgroundColor:"#615e5e"}} title={animal.owners.map(owner => owner.first_name +  ' ' + owner.last_name + ' - ' + owner.display_phone).join(", ") ?? 'N/A'}>
            {animal.owners.map(owner => owner.first_name +  ' ' + owner.last_name + ' - ' + owner.display_phone).join(", ") ?? 'N/A'}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"100px", maxWidth:"100px", backgroundColor:"#615e5e"}} title={animal.microchip || "N/A"}>
            {animal.microchip || "N/A"}
          </Col>
          <Col className="border-top border-start border-bottom" style={{height:"32px", paddingLeft:"3px", paddingTop:"5px", marginTop: "-1px", fontSize:"13px", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", minWidth:"100px", maxWidth:"100px", backgroundColor:"#615e5e"}} title={animal.external_id || "N/A"}>
            {animal.external_id || "N/A"}
          </Col>
          <Col className="border" style={{height:"32px", paddingRight:"3px", paddingTop:"2px", marginTop: "-1px", fontSize:"13px", minWidth:"100px", maxWidth:"100px", borderTopRightRadius:"0.25rem", borderBottomRightRadius:"0.25rem", backgroundColor:"#615e5e"}}>
						<ActionsDropdown disabled={selectedAnimals.length > 0} variant="secondary" title={"Actions"} className="float-end" style={{height:"26px", paddingTop:"0px"}}>
							<LoadingLink
								href={"/" + organization + "/manage/animals/" + animal.id}
								className="d-block"
								isLoading={false}
								label="Animal Details"
								>
								  <FontAwesomeIcon icon={faPaw} className="fas" inverse />
							</LoadingLink>
              <LoadingLink
                  href={"/" + organization + "/manage/animals/edit/" + animal.id}
                  isLoading={false}
                  className="d-block"
                  label="Update Animal"
                >
                  <FontAwesomeIcon icon={faEdit} className="fas" inverse />
                </LoadingLink>
                {animal.hidden ?
                  <LoadingLink
                    onClick={() => showHideAnimal(animal.id, false)}
                    className="d-block"
                    isLoading={false}
                    label="Make Animal Discoverable"
                    >
                    <FontAwesomeIcon icon={faEye} className="fas" inverse />
                  </LoadingLink>
                  :
                  <LoadingLink
                    onClick={() => showHideAnimal(animal.id, true)}
                    className="d-block"
                    isLoading={false}
                    label="Hide Animal From Discovery"
                    >
                    <FontAwesomeIcon icon={faEyeSlash} className="fas" inverse />
                  </LoadingLink>
                  }
						</ActionsDropdown>
          </Col>
        </div>
      </Card>
    )) : ""}
    {objectType === 'owners' || viewMode === 'list' ? <>
    <p className="mt-3">{data.isFetching ? 'Fetching ' + (objectType === 'owners' ? 'Owners' : 'Animals') + '...' :
      <span>
        {objectType === 'owners' && !data.isFetching && data.owners.length === 0 ? 'No Owners found.' : ''}
        {objectType === 'animals' && !data.isFetching && data.animals.length === 0 ? 'No Animals found.' : ''}
      </span>}
    </p>
    <Paginator page={page} numPages={numPages} setPage={setPage} />
    </> : ""}
    <Modal show={showAddressModal} onHide={handleCloseAddressModal}>
      <Modal.Header closeButton>
        <Modal.Title>Address Finder</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <AddressLookup state={state} style={{width: '100%'}} className={"form-control"} setData={setMapSearchOverride} initialBounds={null} handleClose={handleCloseAddressModal} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseAddressModal}>Close</Button>
      </Modal.Footer>
    </Modal>
    <Modal show={showOwnerHideConfirm} onHide={handleOwnerHideClose}>
      <Modal.Header closeButton>
        <Modal.Title>Confirm {showHidden ? " Show" : "Hide"} Owners</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">
          {showHidden ? "Are you sure you would like to make the following owners discoverable?" : "Are you sure you would like to hide the following owners?"}
        </p>
        {data.owners.filter(owner => selectedOwners.includes(owner.id)).map(owner => (
          <div key={owner.id}>{owner.first_name} {owner.last_name}</div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={() => {handleHideOwnerSubmit(showHidden ? false : true)}}>Yes</Button>
        <Button variant="secondary" onClick={handleOwnerHideClose}>Cancel</Button>
      </Modal.Footer>
    </Modal>
    <Modal show={showAnimalHideConfirm} onHide={handleAnimalHideClose}>
      <Modal.Header closeButton>
        <Modal.Title>Confirm {showHidden ? " Show" : "Hide"} Animals</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="mb-2">
          {showHidden ? "Are you sure you would like to make the following animals discoverable?" : "Are you sure you would like to hide the following animals?"}
        </p>
        {data.animals.filter(animal => selectedAnimals.includes(animal.id)).map(animal => (
          <div key={animal.id}>A#{animal.id} - {animal.animal_count > 1 ? <span>{animal.animal_count} {titleCase(animal.species_string)}</span> : <span>{titleCase(animal.species_string)} - {animal.name ? titleCase(animal.name) : "Unknown Name"}</span>}</div>
        ))}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={() => {handleHideAnimalSubmit(showHidden ? false : true)}}>Yes</Button>
        <Button variant="secondary" onClick={handleAnimalHideClose}>Cancel</Button>
      </Modal.Footer>
    </Modal>
	</div>
	)
}

export default PreRegOwnerSearch;
